import React, { useState, useEffect } from 'react';
import { Modal, Switch, message, Button } from 'antd';
import WarningTips from 'components/WarningTips';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { saveProviderSetting } from 'api/klarityReview';
import Star from '../../images/star.svg';

type Props = {
    show: boolean;
    enableReview: boolean;
    onCannel?: () => void;
    onSuccess?: (enableReview: boolean) => void;
    onUpload?: () => void;
};

const SettingsOverviewModal = (props: Props) => {
    const { show, enableReview, onCannel, onSuccess, onUpload } = props;
    const [optIn, setOptIn] = useState<boolean>(enableReview);
    const [loading, setLoading] = useState<boolean>(false);

    // 当 enableReview prop 变化时更新内部状态
    useEffect(() => {
        if (show) {
            setOptIn(enableReview);
        }
    }, [show, enableReview]);

    const onChange = (checked: boolean) => {
        setOptIn(checked);
    };

    const onOk = async () => {
        try {
            setLoading(true);
            const res = await saveProviderSetting({ enableReview: optIn });
            if (res && !res.error) {
                message.success('Save successfully');
                onSuccess?.(optIn);
                onCannel?.();
            } else {
                message.error(res?.error || 'Save failed. Please try again.');
            }
        } catch (error) {
            // Error handling is done by Request class
            message.error('Save failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Settings"
            closable
            className={commonS.modalFixHeightWrap}
            width="650px"
            onCancel={() => {
                onCannel?.();
            }}
            okText="Save"
            open={show}
            destroyOnClose
            onOk={onOk}
            confirmLoading={loading}
        >
            <div className={s.wrap}>
                <div className={s.description}>
                    If you prefer not to receive patient reviews on Klarity, you can opt out below. This setting applies to all future appointments.
                </div>
                <div className={s.switchContainer}>
                    <Switch
                        checked={optIn}
                        onChange={onChange}
                        className={s.switch}
                    />
                    <span className={s.switchLabel}>
                        {optIn ? 'Opt into Klarity Reviews' : 'Opt out of Klarity Reviews'}
                    </span>
                </div>
                {!optIn && (
                    <WarningTips
                        title="Opting into patient reviews is strongly recommended, as providers without reviews may appear lower in search results and receive fewer bookings."
                        isShow
                    />
                )}
                {optIn && (
                    <div className={s.uploadSection}>
                        <div className={s.uploadContent}>
                            <div className={s.uploadText}>
                                <h3 className={s.uploadTitle}>
                                    Got reviews? Bring them to Klarity!
                                </h3>
                                <p className={s.uploadDescription}>
                                    Start building your reputation today by uploading up to{' '}
                                    <strong>50 reviews from your existing patients</strong>. They&apos;ll show up as anonymous (not verified), but they still give new patients a reason to choose you.
                                </p>
                            </div>
                            <div className={s.uploadImage}>
                                <img src={Star} alt="star" />
                            </div>
                            <div className={s.uploadButton}>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        onUpload?.();
                                    }}
                                >
                                    Upload now
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SettingsOverviewModal;
